/**
 * How a trip's timing reads when it has no exact dates.
 *
 * A trip can legitimately have no dates — flexible dates are a supported mode.
 * But "the traveller skipped the date question" and "the traveller said
 * November" are different facts, and the UI used to render both as the same
 * bare "Flexible" / "Dates TBD". The month they told us was captured nowhere
 * and shown nowhere, which is what made the trip feel dateless when it wasn't.
 */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "2026-11" -> "November 2026". Returns null for anything else. */
export function formatTargetMonth(targetMonth: string | undefined | null): string | null {
  if (!targetMonth) return null;
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(targetMonth);
  if (!match) return null;
  return `${MONTH_NAMES[Number(match[2]) - 1]} ${match[1]}`;
}

export function formatTripDates(trip: {
  startDate?: string | null;
  endDate?: string | null;
  targetMonth?: string | null;
}): string {
  if (trip.startDate || trip.endDate) {
    return `${trip.startDate ?? 'TBD'} to ${trip.endDate ?? 'TBD'}`;
  }
  const month = formatTargetMonth(trip.targetMonth);
  // Say what we know, and say plainly what we still don't.
  return month ? `${month} · exact days TBD` : 'Flexible';
}

/**
 * The months a traveller could plausibly be naming, starting with this one.
 *
 * Offered because the date prompt had exactly two shapes — an exact range, or
 * "Dates are flexible" — and neither fits someone who knows the month but not
 * the days. Those travellers pressed skip, whose synthesized message is
 * literally "I don't know that yet", which was false for them and threw the
 * month away.
 *
 * @param today an ISO date (YYYY-MM-DD) standing in for now
 */
const MONTH_PATTERN =
  /\b(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\b/gi;

const MONTH_NAME_TO_INDEX = new Map(
  MONTH_NAMES.flatMap((name, index) => {
    const month = index + 1;
    const full = name.toLowerCase();
    const abbr = full.slice(0, 3);
    return [
      [full, month] as const,
      [abbr, month] as const,
      ...(full === 'september' ? [['sept', month] as const] : []),
    ];
  }),
);

/**
 * Pull month-shaped answers out of free text ("July or August") so the date
 * prompt can offer chips the traveler actually named, not only the next N
 * calendar months from today.
 */
export function extractMentionedMonths(text: string, today: string): string[] {
  const currentYear = Number(today.slice(0, 4));
  const currentMonth = Number(today.slice(5, 7));
  const mentioned = new Set<number>();
  for (const match of text.matchAll(MONTH_PATTERN)) {
    const month = MONTH_NAME_TO_INDEX.get(match[1]!.toLowerCase());
    if (month) mentioned.add(month);
  }
  return [...mentioned]
    .sort((left, right) => left - right)
    .map((month) => {
      const year = month < currentMonth ? currentYear + 1 : currentYear;
      return `${year}-${String(month).padStart(2, '0')}`;
    });
}

export function upcomingMonths(
  today: string,
  count = 8,
  includeValues: readonly string[] = [],
  maxTotal = 12,
): Array<{ value: string; label: string }> {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const merged = new Map<string, { value: string; label: string }>();
  for (const candidate of includeValues) {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(candidate.trim());
    if (match) {
      merged.set(match[0], { value: match[0], label: shortMonthLabel(match[0]) });
    }
  }
  for (const offset of Array.from({ length: count }, (_unused, index) => index)) {
    const monthIndex = month - 1 + offset;
    const value = `${year + Math.floor(monthIndex / 12)}-${String((monthIndex % 12) + 1).padStart(2, '0')}`;
    if (!merged.has(value)) {
      merged.set(value, { value, label: shortMonthLabel(value) });
    }
  }
  return [...merged.values()]
    .sort((left, right) => left.value.localeCompare(right.value))
    .slice(0, maxTotal);
}

/** "2026-11" -> "Nov 2026", for a chip that has to stay narrow. */
export function shortMonthLabel(targetMonth: string): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(targetMonth);
  if (!match) return targetMonth;
  return `${MONTH_NAMES[Number(match[2]) - 1]!.slice(0, 3)} ${match[1]}`;
}
